// T-S1-A W7 · 产品模块 TypeScript types

export type ProductStatus = "draft" | "published" | "archived";
// PMODE-1 · engine=四要素算成本（默认）/ manual=手填基础成本/人
export type PricingMode = "engine" | "manual";
export type ScheduleStatus =
  | "planned"
  | "open_booking"
  | "closed"
  | "cancelled"
  | "departed"
  | "completed";

// INTERCITY-1 · 城际用车段
export type IntercityDirection = "round_trip" | "a_to_b" | "b_to_a";
export interface IntercityLegIn { route_id: string; direction: IntercityDirection }
export interface IntercityLegRead extends IntercityLegIn {
  route_name_cn: string; route_name_en: string;
  one_way_cents: number;
  amount_cents: number;
}

export interface WeeklyCadence {
  days_of_week: number[]; // 0=Sun … 6=Sat
  weeks_ahead: number;
}

// LIST-1 · 接送与导游卡
export interface PickupStop {
  time: string; // HH:MM 24h（前端显示自转 12h）
  name: string;
  address: string;
}
export interface DropOff {
  name: string;
  address: string;
}

/** GYGUX-1 · 散团点（列表，顺序即送客顺序）：名称（英文，给平台看）+ 地址 + 可选时间 HH:MM（24h，空 = 不填）。和上车点同一个表格组件。 */
export interface DropOffStop {
  name: string;
  address: string;
  time?: string | null;
}
export const GUIDE_LANGUAGE_LABEL: Record<string, string> = { en: "英语", zh: "中文", es: "西班牙语", ja: "日语" };
export const GUIDE_LANGUAGE_OPTIONS = ["en", "zh", "es", "ja"] as const;

// LIST-1 · 行程日餐食三格（含在团费里的打勾）
export interface Meals {
  breakfast: boolean;
  lunch: boolean;
  dinner: boolean;
}

// LIST-1 · leg.attraction_ids 内联解析（detail 端点填充）
export interface AttractionMini {
  id: string;
  name_cn: string;
  name_en: string | null;   // ENNAME-2：景点英文名可空
  is_paid: boolean;
  parent_name_cn: string | null;
}

/** TIXFEE-1 · 景点费用类型：已含=门票含在团费里（公司代买出票）/ 必付=到景点现场另付（强制）/ 自费=客人自愿另付。
 *  必选项、没有默认值：null = 未选（只会出现在老产品上），任何地方都不许当成「已含」显示。 */
export type FeeType = "included" | "must_pay" | "self_pay";
export const FEE_TYPE_LABEL: Record<FeeType, string> = { included: "已含", must_pay: "必付", self_pay: "自费" };

/** TIXFEE-1 · 单日弹窗每个景点一行：停留时长（分钟）+ 费用类型 */
export interface AttractionStop {
  attraction_id: string;
  stay_minutes: number | null;
  fee_type: FeeType | null;
  /** TIXSEL-1 · 「已含」景点选定的那张票（有票种必选；免费景点 / 自费 / 必付不选，传了后端清掉） */
  ticket_type_id?: string | null;
}

/** TIXSEL-1 · 资源绑定「门票」格一行：后端按 attraction_meta 现算（只读） */
export interface LegTicketLine {
  attraction_id: string;
  attraction_name: string;
  fee_type: FeeType | null;
  /** selected 已含 + 已选票 / free 已含 + 免费景点 / missing 已含 + 有票种没选 / self_pay / must_pay / unset */
  state: "selected" | "free" | "missing" | "self_pay" | "must_pay" | "unset";
  ticket_type_id: string | null;
  ticket_name: string | null;
  rate_cents: number | null;
}

export interface ProductLegRead {
  id: string;
  tenant_id: string;
  product_id: string;
  leg_template_id: string | null;
  leg_order: number;
  day_offset_start: number;
  day_offset_end: number;
  default_hotel_id: string | null;
  no_overnight?: boolean;   // HOTELGATE-1 · 这一晚明确不住（非套团；和酒店二选一）
  default_ticket_ids: string[] | null;
  default_ticket_names?: string[] | null; // detail 端解析·同序
  /** TIXFEE-1 · 与 default_ticket_names 同序：该票种所属景点被标必付 / 自费时给出费用类型（下单不出票），否则 null */
  default_ticket_fee_flags?: (FeeType | null)[] | null;
  /** TIXSEL-1 · 资源绑定「门票」格（只读）：每个景点的状态 + 选定票名 / 单位成本 */
  ticket_lines?: LegTicketLine[] | null;
  // W7
  hotel_includes_breakfast: boolean;
  hotel_nights_count: number;
  dining_arrangement: string | null;
  // A 方案：逐日内联字段（canvas page-42 step ②）
  day_title: string | null;
  sightseeing_spots: string[] | null;
  drive_time_text: string | null;
  day_tip: string | null;
  day_description: string | null; // PUBPAGE · 对外介绍文案（公开页/邮件读它，不与 day_tip 合并）
  day_count: number;
  meeting_time: string | null;    // DEPT-1 · 集合时间（HH:MM）
  pickup_location: string | null; // DEPT-1 · 集合地点
  // LIST-1 · 景点关联景点库（旧 sightseeing_spots 文字保留只读参考）+ 餐食三格
  attraction_ids: string[] | null;
  attractions?: AttractionMini[] | null; // detail 端解析·同序
  /** TIXFEE-1 · 按 attraction_ids 顺序每个景点的停留时长 + 费用类型 */
  attraction_stops?: AttractionStop[] | null;
  /** SITE-E1 · 当天车程（公里 / 分钟，可空；前端填小时可带小数，存分钟） */
  drive_km?: number | null;
  drive_minutes?: number | null;
  meals: Meals | null;
  /** TAOTUAN-1 · 挂线路的产品：当晚住 / 酒店 / 集合时间 / 上车点由路段派生（只读）；source=route|transfer_pickup|transfer_dropoff|product */
  logistics?: LegLogistics | null;
}

export interface LegLogistics {
  overnight_city: string | null;
  hotel_id?: string | null;
  meet_local_time: string | null;
  meet_place: string | null;
  tz: string | null;
  segment_name: string | null;
  /** TAOTUAN-2 后端加：路段当晚不住（行程结束 / 回程） */
  no_overnight?: boolean;
  source: "route" | "transfer_pickup" | "transfer_dropoff" | "product";
}

/** 旧字段（只读镜像；老调用方 / 老前端还认）。TAOTUAN-6 起以 pickup_mode / dropoff_mode 为准。 */
export type TransferMode = "none" | "pickup_before" | "dropoff_after" | "both";
/** TAOTUAN-6 · 接机：不含 / 单接机（不算行程天、不排酒店）/ 接机 + 住一晚（算第 1 天、排酒店） */
export type PickupMode = "none" | "service" | "stay";
/** TAOTUAN-6 · 送机：不含 / 含送机（算最后一天、不排酒店） */
export type DropoffMode = "none" | "day";

/** 条件发团（API_MAP 第六节）：days = 星期（同 weekly_cadence_json.days_of_week：0=周日…6=周六） */
export interface ConditionalRule {
  days: number[];
  depends_on_route_line_id: string;
  depends_on_board_day: number;
}

export interface ProductRead {
  id: string;
  tenant_id: string;
  /** TICKETOPT-1 · 门票产品儿童年龄范围（默认 3–12；3 岁以下免票不占位）；其它类型忽略 */
  child_age_min?: number | null;
  child_age_max?: number | null;
  code: string;
  short_code: string | null; // SKU-1 · 业务代码（人叫的名字·选填·随时改·不参与平台对接）
  name_cn: string;
  name_en: string | null;   // ENNAME-1：选填（存量里可能是中文顶替值，是否真英文名看 name_en_missing）
  /** ENNAME-1 · 后端判定：没有真英文名（空 / 等于中文名 / 含中文 / 不含拉丁字母） */
  name_en_missing?: boolean;
  /** CONFIRM-1 · 确认方式：instant 即时确认 / manual 人工确认 / null 未设置（按人工确认处理） */
  confirm_mode?: ConfirmMode | null;
  description: string | null;
  departure_city: string;
  end_city: string;
  status: ProductStatus;
  total_days: number;
  // W7
  category: string | null;
  max_pax_per_departure: number;
  min_pax: number;
  mandatory_fee_cents: number;
  // PMODE-1 · 定价方式：engine=四要素算成本（默认）/ manual=手填成本就是套餐成本/人
  pricing_mode: PricingMode;
  manual_cost_per_person_cents: number | null; // manual 时非空·engine 时恒 None
  // COSTADJ-1 · 产品级成本调整（只对 engine 生效；非成本可见角色读到 null）：午餐餐标（每人每餐·分）/ 本产品车队日成本（含司导·分/天）单独调整；null = 没填 / 跟随车辆资料
  lunch_cost_per_person_cents?: number | null;
  fleet_daily_cost_override_cents?: number | null;
  // INTERCITY-1 · 所选城际用车段（含线路名 / 方向 / 当前金额·分/人；金额由后端按价目算）
  intercity_legs?: IntercityLegRead[];
  weekly_cadence_json: WeeklyCadence | null;
  // TAOTUAN-1 · 套团
  route_line_id?: string | null;
  board_day?: number | null;
  alight_day?: number | null;
  vehicle_tier?: string | null;
  transfer?: TransferMode;
  // TAOTUAN-6 · 接送机（新字段为真源）：pickup_hotel_id = 个别团型改「接机当晚酒店」（空 = 用默认）
  pickup_mode?: PickupMode;
  dropoff_mode?: DropoffMode;
  pickup_hotel_id?: string | null;
  // TRANSCOST-1 · 接机 / 送机成本（每趟全包·含油 + 司机·美分）；空 = 用线路默认；只有成本可见角色读得到，其它角色永远是 null
  pickup_cost_cents?: number | null;
  dropoff_cost_cents?: number | null;
  conditional_rule?: ConditionalRule | null;
  // CHANFIX-1（migration 0130）· 产品出发地时区——Viator 截单/GYG dateTime 按它构造
  // 当地时间；与运营口径（太平洋时间）是两件事。
  time_zone: string;
  // SITE-E1 · 手机站展示卡（只给手机站用，不影响平台上架）。分类「多日游 / 一日游」不存，按 total_days 算
  public_title: string | null;     // 空 = 用 name_cn
  public_tagline: string | null;   // 一句卖点
  is_transfer: boolean;            // 接送机
  is_small_group: boolean;         // 小团
  small_group_max: number | null;  // 小团人数上限
  requires_passport?: boolean;     // TRAV-3 · 需要收集护照（名单多一列护照号；默认关）
  seasonal_notes: string | null;   // 季节说明
  pickup_point: string | null;
  drop_off_point: string | null;
  // LIST-1 · 接送与导游卡（旧 pickup_point/drop_off_point 保留只读对照）
  pickup_stops: PickupStop[] | null;
  drop_off: DropOff | null;   // 旧单个散团点：GYGUX-1 起只读（迁成了 drop_off_stops 第一条）
  drop_off_stops?: DropOffStop[] | null;   // GYGUX-1 · 散团点列表（真源）
  guide_languages: string[] | null;
  emergency_contact: string | null;
  /** CTRIP-P1 · 紧急联系人拆两格；旧 emergency_contact 自由文字只读保留 */
  emergency_contact_name?: string | null;
  emergency_contact_mobile?: string | null;
  default_vehicle_id: string | null;
  // VEHBIND · 可用车型绑定（车型 label 数组·NULL/空=不限=全车队）·库存池按此过滤车座
  allowed_vehicle_models: string[] | null;
  // VEHVIEW · 指定车辆绑定（车 uuid 数组·与车型并存构成混合可用池·NULL/空=无指定）
  allowed_vehicle_ids: string[] | null;
  customer_notice: string | null; // PRD1 客户须知（→ 凭证继承）
  image_url: string | null;       // PRD3 主图
  tags: string[];                 // PRD3 分类标签
  // SITE-A · C 端富化字段（null=从未录入·[]=录入后清空）
  highlights: string[] | null;
  includes: string[] | null;
  excludes: string[] | null;
  created_at: string;
  updated_at: string;
  // 列表聚合
  schedule_count?: number;       // 已排期数
  leg_count?: number;            // 行程段数
  fill_rate_month?: number | null; // 本月满团率 %
}

// SITE-A · 图集单条（product_media 子表）
export interface ProductMediaRead {
  id: string;
  url: string;
  kind: string;
  sort_order: number;
  // LIST-1 · 尺寸校验（GET 端点算好·不是 DB 列）
  width: number | null;
  height: number | null;
  platform_ok: boolean | null;
  platform_reason: "portrait" | "too_narrow" | null;
}

export interface ProductMediaItem {
  url: string;
  kind?: string;
  width?: number | null;
  height?: number | null;
}

/** SITE-E1 · 手机站在不在展示（后端 consumer_site.mobile_site_status 唯一函数；口径 = C 端目录） */
export interface MobileSiteStatus {
  showing: boolean;
  reason: "unpublished" | "off_shelf" | "no_image" | "no_price" | null;
  reason_label: string | null; // 未发布 / 已下架 / 缺主图 / 算不出价
}

export type TransferCostSource = "none" | "product" | "line" | "unset";
export interface TransferCostItemView { needed: boolean; cents: number | null; source: TransferCostSource }
export interface TransferCostView { pickup: TransferCostItemView; dropoff: TransferCostItemView; missing: boolean; pax_estimate: number }

export interface ProductWithLegs extends ProductRead {
  mobile_site?: MobileSiteStatus | null;
  legs: ProductLegRead[];
  /** 已铺排期日期区间（detail 端点填充·编辑向导回填发团起止·null=无排期） */
  schedule_date_from?: string | null;
  schedule_date_to?: string | null;
  /** TAOTUAN-1 · 所属线路（没挂 = null） */
  route_line?: { id: string; name: string; total_days: number } | null;
  /** TRANSCOST-1 · 接送机成本取值结果（产品填的 / 线路默认 / 未填）；仅成本可见角色有，其它角色 null。missing = 选了接机 / 送机但没成本（红牌，存量产品） */
  transfer_cost?: TransferCostView | null;
  /** SITE-A · 图集（detail 端点按 sort_order 填充） */
  media: ProductMediaRead[];
  /** IMGLIB-1 · 产品图片（图片库挂接）：宣传长图 + 产品相册，封面 = 相册第一张（旧 media 是旧表，只读保留一个版本） */
  images?: import("../mediaLibrary").ProductImages;
}

export interface ProductStats {
  total: number;
  published: number;
  draft: number;
  archived: number;
  scheduled_this_week: number;
  capacity_this_month: number;
}

export interface ProductScheduleRead {
  id: string;
  tenant_id: string;
  product_id: string;
  departure_date: string; // YYYY-MM-DD
  status: ScheduleStatus;
  max_pax: number;
  booked_pax: number;
  available_pax: number;
  cutoff_date: string | null;
  notes: string | null;
  price_cents: number | null; // PRD4b 该出发日售价（分·NULL=未设）
  option_id?: string | null;  // OPT-B3a · 团期所属选项
  // OPT-B3a §5 · 已派车（订单 assigned_vehicle_id 聚合）：没派 = null；多辆时取载人最多的那辆；POST / :generate 返回里恒为 null / 0
  assigned_vehicle?: { vehicle_id: string; plate: string | null; name: string | null; model_label_cn: string | null; seats: number | null } | null;
  assigned_vehicle_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ProductAvailability {
  query_date: string;
  schedule_id: string | null;
  schedule_status: string | null;
  schedule_available_pax: number | null;
  hotel_available: number | null;
  vehicle_available: number | null;
  ticket_available: number | null;
  composite_available: number | null;
}

// Payloads
export interface ProductCreatePayload {
  /** WANLE-1 · 门票产品：category="ticket" 时必填的景区 id；其它类型不能带 */
  attraction_id?: string;
  /** TICKETOPT-1 · 门票产品儿童年龄范围（默认 3–12） */
  child_age_min?: number | null;
  child_age_max?: number | null;
  /** WIZDRAFT-1 · 向导里存过的草稿 id：产品建成功的同一事务里后端把这条草稿删掉；不传 = 和以前一样 */
  wizard_draft_id?: string;
  // SKU-1 · code 已从创建payload删除——SKU 一律服务端自动编号 PRD-NNNNN，
  // 客户端不再能指定（传了后端也 422，见 schemas/product.py::ProductCreate）。
  short_code?: string; // 业务代码（选填·自由文本·随时改·不参与平台对接）
  name_cn: string;
  name_en?: string | null;   // ENNAME-1：选填
  confirm_mode?: ConfirmMode;   // CONFIRM-1 · 建品即「上架」必带（草稿可空）；向导第 1 步必选
  description?: string;
  departure_city: string;
  end_city: string;
  status?: ProductStatus;
  category?: string;
  max_pax_per_departure?: number;
  min_pax?: number;
  mandatory_fee_cents?: number;
  // PMODE-1 · 不传=服务端默认 engine；manual 时 manual_cost_per_person_cents 必填正数
  pricing_mode?: PricingMode;
  manual_cost_per_person_cents?: number | null;
  lunch_cost_per_person_cents?: number | null;        // COSTADJ-1 · 不传 / null = 没填
  fleet_daily_cost_override_cents?: number | null;    // COSTADJ-1 · 不传 / null = 跟随车辆资料
  intercity_legs?: IntercityLegIn[] | null;           // INTERCITY-1 · 只传线路 + 方向，金额由后端算
  weekly_cadence_json?: WeeklyCadence | null;
  time_zone?: string; // CHANFIX-1 · 不传=服务端默认 America/Los_Angeles
  // TAOTUAN-1 · 套团（不套团的产品一个都不传）
  route_line_id?: string;
  board_day?: number;
  alight_day?: number;
  vehicle_tier?: string;
  transfer?: TransferMode;
  pickup_mode?: PickupMode;           // TAOTUAN-6
  dropoff_mode?: DropoffMode;
  pickup_hotel_id?: string | null;
  pickup_cost_cents?: number | null;   // TRANSCOST-1 · 选了接机 / 送机必填（自己填或线路默认带出）
  dropoff_cost_cents?: number | null;
  conditional_rule?: ConditionalRule | null;
  pickup_point?: string;
  drop_off_point?: string;
  default_vehicle_id?: string | null;
  allowed_vehicle_models?: string[] | null; // VEHBIND 可用车型绑定（空/null=不限）
  allowed_vehicle_ids?: string[] | null;     // VEHVIEW 指定车辆绑定（空/null=无指定）
  customer_notice?: string | null; // PRD1 客户须知
  image_url?: string | null;       // PRD3 主图
  tags?: string[];                 // PRD3 分类标签
  legs?: ProductLegCreatePayload[];
  // WIZSYNC-1 · 接送与导游（选填）——后端 create_product 早就读这四个字段
  // （schemas/product.py::ProductCreate 早就收），前端类型定义之前漏加，向导传了
  // 会被 tsc 当成多余字段拒绝
  pickup_stops?: PickupStop[] | null;
  drop_off?: DropOff | null;
  drop_off_stops?: DropOffStop[] | null;   // GYGUX-1 · 散团点列表（整块替换；不传 = 不动）
  guide_languages?: string[] | null;
  emergency_contact?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_mobile?: string | null;
}

export interface ProductLegCreatePayload {
  leg_template_id?: string | null;
  leg_order?: number;
  // PRODEDIT：允许显式 null 清空已绑酒店（PATCH 用 exclude_unset，undefined=不改·null=清空，
  // 详情页③资源绑定「当日返程（无住宿）」要能把已绑酒店真的清掉，不能只是不改）
  default_hotel_id?: string | null;
  no_overnight?: boolean;   // HOTELGATE-1 · true = 这一晚不住（后端同时清酒店）；false = 取消不住
  default_ticket_ids?: string[];
  hotel_includes_breakfast?: boolean;
  hotel_nights_count?: number;
  dining_arrangement?: string | null;
  // A 方案：逐日内联字段
  day_title?: string | null;
  sightseeing_spots?: string[] | null;
  drive_time_text?: string | null;
  day_tip?: string | null;
  day_description?: string | null; // PUBPAGE
  day_count?: number;
  meeting_time?: string | null;    // DEPT-1 · 集合时间（HH:MM / 8:00 AM / HH:MM:SS·后端统一规整存 HH:MM）
  pickup_location?: string | null; // DEPT-1 · 集合地点
  attraction_ids?: string[] | null; // LIST-1
  attraction_stops?: AttractionStop[] | null; // TIXFEE-1 · 整块替换，必须覆盖当天每个景点且都选了费用类型（否则 422）
  drive_km?: number | null;      // SITE-E1 · null = 清空
  drive_minutes?: number | null; // SITE-E1 · 整分钟
  meals?: Meals | null;             // LIST-1
}

// PUBPAGE 工单 · 产品公开行程页分享发送
export interface ShareEmailPayload {
  recipients: string[];
  message?: string | null;
}

export interface ShareEmailResult {
  recipient: string;
  ok: boolean;
  error: string | null;
}

export interface ShareEmailResponse {
  public_url: string;
  results: ShareEmailResult[];
}

export interface ProductUpdatePayload {
  confirm_mode?: ConfirmMode;   // CONFIRM-1 · 只提交本块字段；改为 manual 有「要求即时确认」的平台映射 → 409 CONFIRM_MODE_LOCKED
  // REASON-1 · 改价格类字段 / 下架 / 归档时必带原因（后端 422「请填写原因」）；上架选填
  reason_code?: string;
  reason?: string;
  // TAOTUAN-6 · 接送机（产品详情「接送机」卡只提交这三个字段）
  pickup_mode?: PickupMode;
  dropoff_mode?: DropoffMode;
  pickup_hotel_id?: string | null;
  pickup_cost_cents?: number | null;   // TRANSCOST-1 · 显式 null = 清回线路默认
  dropoff_cost_cents?: number | null;
  // SKU-1 · code 界面无入口（管理员纠错通道，仅 owner/tech_admin 经 API 直改，
  // 见工单 §4）——这里故意不声明，普通编辑表单不该能拼出这个字段。
  short_code?: string | null; // 业务代码（undefined=不改·null/""=清空·随时改）
  name_cn?: string;
  name_en?: string | null;   // ENNAME-1：null / 空 = 清空
  description?: string | null;
  departure_city?: string;
  end_city?: string;
  status?: ProductStatus;
  category?: string | null;
  max_pax_per_departure?: number;
  min_pax?: number;
  mandatory_fee_cents?: number;
  // PMODE-1 · undefined=不改；manual 时 manual_cost_per_person_cents 必须同批带正数
  pricing_mode?: PricingMode;
  manual_cost_per_person_cents?: number | null;
  lunch_cost_per_person_cents?: number | null;        // COSTADJ-1 · undefined=不改；null=清空（没填）
  fleet_daily_cost_override_cents?: number | null;    // COSTADJ-1 · undefined=不改；null=恢复跟随车辆资料
  intercity_legs?: IntercityLegIn[] | null;           // INTERCITY-1 · undefined=不改；[]=清空；整体替换本字段
  weekly_cadence_json?: WeeklyCadence | null;
  time_zone?: string; // CHANFIX-1 · undefined=不改
  // SITE-E1 · 手机站展示卡（undefined=不改；null=清空；只提交本卡字段）
  public_title?: string | null;
  public_tagline?: string | null;
  is_transfer?: boolean;
  is_small_group?: boolean;
  small_group_max?: number | null;
  requires_passport?: boolean; // TRAV-3
  seasonal_notes?: string | null;
  pickup_point?: string | null;
  drop_off_point?: string | null;
  default_vehicle_id?: string | null;
  allowed_vehicle_models?: string[] | null; // VEHBIND（[]=清空转不限·非空=按车型过滤）
  allowed_vehicle_ids?: string[] | null;     // VEHVIEW（[]=清空·非空=指定车 uuid）
  customer_notice?: string | null; // PRD1 客户须知
  image_url?: string | null;       // PRD3 主图
  tags?: string[];                 // PRD3 分类标签
  // SITE-A · undefined=不改·[]=清空（与 tags 同一纪律）
  highlights?: string[] | null;
  includes?: string[] | null;
  excludes?: string[] | null;
  // LIST-1 · 接送与导游卡——只提交本卡这 4 个字段（红线：不整体 PATCH）
  pickup_stops?: PickupStop[] | null;
  drop_off?: DropOff | null;
  drop_off_stops?: DropOffStop[] | null;   // GYGUX-1 · 散团点列表（整块替换；不传 = 不动）
  guide_languages?: string[] | null;
  emergency_contact?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_mobile?: string | null;
}

export interface ScheduleGeneratePayload {
  date_from: string; // YYYY-MM-DD
  date_to: string;
  days_of_week?: number[] | null;
  max_pax?: number | null;
  status?: ScheduleStatus;
  cutoff_days_before?: number | null;
  option_id?: string | null;   // OPT-C2 · 挂到哪个选项（不传 = 默认选项）
}

export interface ScheduleGenerateResult {
  created: number;
  skipped: number;
  schedules: ProductScheduleRead[];
}

export interface ProductScheduleCreatePayload {
  option_id?: string | null;   // OPT-C2 · 挂到哪个选项（不传 = 默认选项）
  departure_date: string;
  status?: ScheduleStatus;
  max_pax: number;
  booked_pax?: number;
  cutoff_date?: string | null;
  notes?: string | null;
  price_cents?: number | null;
}

export interface ProductScheduleUpdatePayload {
  departure_date?: string;
  status?: ScheduleStatus;
  max_pax?: number;
  booked_pax?: number;
  cutoff_date?: string | null;
  notes?: string | null;
  price_cents?: number | null;
}

/** CONFIRM-1 · 产品确认方式（必选项，不给默认值） */
export type ConfirmMode = "instant" | "manual";

export interface ProductListQuery {
  confirm_mode?: ConfirmMode | "unset";   // CONFIRM-1 · 列表筛选（unset = 未设置）
  status?: ProductStatus;
  category?: string;
  q?: string;
  limit?: number;
  offset?: number;
}
